import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Search,
    Loader2,
    MapPin,
    Phone,
    Mail,
    Monitor,
    Users,
    CalendarClock,
    BadgeCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCENT = "#C2410C";

export default function Teachers() {
    const [postcode, setPostcode] = useState("");
    const [loading, setLoading] = useState(false);
    const [lookup, setLookup] = useState(null); // { partner, territory, postcode_district, message } | null

    const onSearch = async (e) => {
        e.preventDefault();
        const value = postcode.trim();
        if (!value) {
            toast.error("Please enter your UK postcode.");
            return;
        }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/teachers/nearest`, {
                params: { postcode: value },
            });
            setLookup(r.data);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Could not look up that postcode. Please check and try again.";
            toast.error(typeof msg === "string" ? msg : "Lookup failed");
            setLookup(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="teachers"
            className="relative px-6 md:px-10 py-16 md:py-24 bg-[#F1E8D5]"
            data-testid="teachers-section"
        >
            <div className="max-w-7xl mx-auto">
                <div className="reveal mb-8 md:mb-12 max-w-3xl">
                    <h2 className="font-display tracking-tighter font-black leading-[0.95] text-5xl md:text-6xl lg:text-7xl text-black">
                        Find a vocal
                        <br />
                        coach
                        <em className="not-italic bg-[#1B4D3E] text-white px-3 -rotate-1 inline-block ml-2">near you.</em>
                    </h2>
                    <p className="mt-6 text-stone-700 text-lg max-w-xl">
                        Enter your UK postcode and we&rsquo;ll connect you with the
                        Certified My First Single Partner covering your area.
                    </p>
                </div>

                {/* Search box */}
                <form
                    onSubmit={onSearch}
                    className="relative bg-white text-black rounded-3xl border-2 border-black shadow-[6px_6px_0_0_#0A0A0A] p-5 md:p-6 reveal max-w-2xl"
                    data-testid="teachers-search-form"
                >
                    <label htmlFor="postcode" className="block font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600 mb-2">
                        Your UK postcode
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MapPin
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C2410C]"
                            />
                            <input
                                id="postcode"
                                type="text"
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                                placeholder="e.g. M1 1AE"
                                autoComplete="postal-code"
                                spellCheck={false}
                                className="w-full pl-11 pr-4 py-3.5 bg-[#F1E8D5] border-2 border-black rounded-full text-black font-medium text-lg outline-none focus:bg-white placeholder:text-stone-500"
                                data-testid="teachers-postcode-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#1B4D3E] disabled:opacity-60 text-white font-medium rounded-full px-7 py-3.5 transition-all active:scale-95 border-2 border-black"
                            data-testid="teachers-search-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Searching
                                </>
                            ) : (
                                <>
                                    <Search size={18} />
                                    Find my Partner
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Results */}
                {lookup !== null && (
                    <div className="mt-12 reveal in" data-testid="teachers-results">
                        {lookup.partner ? (
                            <>
                                <div className="mb-5 flex flex-wrap items-baseline gap-x-3">
                                    <span className="font-mono text-[11px] uppercase tracking-widest font-bold text-stone-600">
                                        Your Certified Partner in
                                    </span>
                                    {lookup.territory && (
                                        <span className="font-display font-black text-xl tracking-tight">
                                            {lookup.territory.territory_name}
                                        </span>
                                    )}
                                </div>
                                <div className="grid md:grid-cols-2 gap-6 items-start">
                                    <TeacherCard
                                        teacher={lookup.partner}
                                        accent={ACCENT}
                                    />
                                    <div className="text-stone-700 leading-relaxed md:pt-4">
                                        <p className="text-lg">
                                            You&rsquo;re matched with{" "}
                                            <strong className="text-black">{lookup.partner.name}</strong>,
                                            your Certified My First Single Partner covering{" "}
                                            {lookup.territory?.territory_name || "your area"}.
                                        </p>
                                        {lookup.partner.consultation_booking_url && (
                                            <p className="mt-3 text-sm text-stone-600">
                                                Book a free consultation directly using the
                                                button on their profile — no waiting, no forms.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <NoAreaState
                                message={lookup.message}
                                territory={lookup.territory}
                                district={lookup.postcode_district}
                            />
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

function TeacherCard({ teacher: t, accent }) {
    return (
        <article
            className="group relative bg-white text-black rounded-3xl border-2 border-black shadow-[6px_6px_0_0_#0A0A0A] transition-all overflow-hidden flex flex-col max-w-md"
            data-testid="partner-card"
        >
            {/* Portrait spotlight */}
            <div className="relative aspect-[4/5] w-full overflow-hidden border-b-2 border-black">
                {t.photo_url ? (
                    <img
                        src={t.photo_url}
                        alt={`Portrait of ${t.name}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover grayscale-[0.15] transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                ) : (
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(135deg, ${accent} 0%, #0A0A0A 100%)`,
                        }}
                    />
                )}
                <div
                    className="absolute inset-0 mix-blend-multiply opacity-70"
                    style={{
                        background: `radial-gradient(ellipse at 30% 25%, transparent 0%, transparent 35%, ${accent}CC 100%)`,
                    }}
                    aria-hidden
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.15) 35%, transparent 60%)",
                    }}
                    aria-hidden
                />

                {/* Certified badge */}
                {t.certified_partner && (
                    <span
                        className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono font-bold uppercase tracking-widest border-2 border-black text-black bg-[#D4A017] z-10"
                        data-testid="partner-certified-badge"
                    >
                        <BadgeCheck size={12} />
                        Certified
                    </span>
                )}

                {/* Name plate */}
                <div className="absolute left-5 right-5 bottom-4 z-10">
                    <h3
                        className="font-display font-black tracking-tighter text-[28px] leading-[1.02] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
                        data-testid="partner-name"
                    >
                        {t.name}
                    </h3>
                    {t.territory_name && (
                        <div className="mt-1 flex items-center gap-1.5 text-[12px] text-white/85">
                            <MapPin size={11} />
                            <span>{t.territory_name}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col flex-1">
                {t.bio && (
                    <p className="text-stone-700 text-[14px] leading-relaxed">
                        {t.bio}
                    </p>
                )}

                {t.genres && t.genres.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {t.genres.slice(0, 6).map((g) => (
                            <span
                                key={g}
                                className="px-2.5 py-0.5 rounded-full border border-black text-[11px] font-medium bg-[#F1E8D5]"
                            >
                                {g}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-5 pt-4 border-t border-black/10 grid grid-cols-2 gap-3 text-xs text-stone-700">
                    {t.experience_years != null && (
                        <Meta label="Experience" value={`${t.experience_years}+ yrs`} />
                    )}
                    {t.rate_per_hour_gbp != null && (
                        <Meta label="From" value={`£${t.rate_per_hour_gbp}/hr`} />
                    )}
                    <Meta
                        label="Format"
                        value={
                            <span className="inline-flex items-center gap-1.5">
                                {t.in_person && <Users size={11} />}
                                {t.online && <Monitor size={11} />}
                                <span>
                                    {[t.in_person && "In\u2011person", t.online && "Online"]
                                        .filter(Boolean)
                                        .join(" + ")}
                                </span>
                            </span>
                        }
                    />
                </div>

                {/* Primary consultation CTA */}
                {t.consultation_booking_url && (
                    <a
                        href={t.consultation_booking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#C2410C] text-white font-medium rounded-full px-5 py-3 transition-all active:scale-95 border-2 border-black shadow-[4px_4px_0_0_#C2410C] hover:shadow-[2px_2px_0_0_#C2410C] hover:translate-x-[2px] hover:translate-y-[2px]"
                        data-testid="partner-consultation-btn"
                    >
                        <CalendarClock size={16} />
                        Book a consultation
                    </a>
                )}

                {(t.phone || t.email) && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {t.phone && (
                            <a
                                href={`tel:${t.phone.replace(/\s+/g, "")}`}
                                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border-2 border-black bg-white hover:bg-[#F1E8D5] transition-colors"
                                data-testid="partner-call-btn"
                            >
                                <Phone size={12} />
                                Call
                            </a>
                        )}
                        {t.email && (
                            <a
                                href={`mailto:${t.email}`}
                                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border-2 border-black bg-white hover:bg-[#F1E8D5] transition-colors"
                                data-testid="partner-email-btn"
                            >
                                <Mail size={12} />
                                Email
                            </a>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[9px] uppercase tracking-widest font-bold text-stone-500">
                {label}
            </div>
            <div className="text-[13px] font-medium text-black mt-0.5">{value}</div>
        </div>
    );
}

function NoAreaState({ message, territory, district }) {
    return (
        <div
            className="bg-white border-2 border-black rounded-3xl p-8 md:p-10 max-w-2xl shadow-[6px_6px_0_0_#0A0A0A]"
            data-testid="teachers-no-area"
        >
            <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-[#C2410C]">
                {district ? `Postcode district ${district}` : "Postcode checked"}
                {territory && ` · ${territory.territory_name}`}
            </div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tighter leading-[1.05]">
                We&rsquo;re not in your area just yet.
            </h3>
            <p className="mt-5 text-stone-700 text-[15px] leading-relaxed whitespace-pre-line">
                {message?.replace("We\u2019re not in your area just yet. ", "")}
            </p>
        </div>
    );
}
